/**
 * Swipe View — touch-drag geometry (SMASH-1978).
 *
 * The thumb-follow gesture's arithmetic, kept as pure functions with no React
 * and no DOM — the same reason `slotWindow.js` is: the properties that actually
 * matter here (the track can never travel further than the slot window can
 * show; the release decision is a majority-of-viewport rule with a derived
 * boundary) are worth unit-testing and mutation-testing without mounting
 * anything, and worth reading without a component around them.
 *
 * Sign convention throughout, chosen once so it cannot drift between the
 * handlers and this file:
 *
 *   `dragOffset` is the px the TRACK has been pulled from its resting position,
 *   positive DOWNWARD — i.e. `touch.clientY - startY`. Pulling the finger down
 *   (positive) drags the track down and reveals the PREVIOUS slide above;
 *   pulling up (negative) reveals the NEXT slide below.
 *
 * That is the opposite sign to the `dy` the pre-SMASH-1978 touchend handler
 * used (`startY - clientY`), and deliberately so: this value is applied
 * directly to a `translateY`, so it reads correctly as "where the track is"
 * rather than needing a mental negation at the point of use.
 */

/**
 * The flick accelerator's two bounds.
 *
 * `SWIPE_THRESHOLD_PX` is inherited unchanged from the pre-drag implementation,
 * where it was the WHOLE rule: any touch that moved more than 60px advanced a
 * slide, at any speed.
 *
 * `SWIPE_MAX_DURATION_MS` is NEW in SMASH-1978 and is load-bearing rather than
 * cosmetic — see `decideSnap`. Without a duration bound the 60px rule would
 * subsume the area rule entirely (every drag past 60px would commit, so the
 * majority-of-viewport test could never decide anything) and this ticket would
 * have changed nothing. Bounding it to a flick is what leaves the area rule
 * something to govern.
 */
export const SWIPE_THRESHOLD_PX = 60;
export const SWIPE_MAX_DURATION_MS = 600;

/**
 * How far a touch may travel and still count as a tap rather than a drag.
 *
 * Browsers apply their own slop of roughly this size before they stop
 * synthesising a `click` from a touch sequence, but the exact figure is not
 * specified and `preventDefault` on touchmove suppresses the synthetic click in
 * some engines and not others. The viewer's gesture layer carries a real
 * `onClick` (tap-to-toggle play/pause), so it needs its own answer rather than
 * inheriting whichever one the engine happens to use: without it every swipe
 * would also pause the video.
 */
export const TAP_SLOP_PX = 10;

/**
 * The gesture clock.
 *
 * `performance.now()` rather than `Date.now()`, for two reasons that both
 * matter here. It is monotonic, so a system clock adjustment mid-gesture cannot
 * produce a negative duration and hand the flick accelerator a free pass. And
 * it keeps this measurement off the wall clock that testing-library's own
 * `waitFor` reads for its timeout — freezing `Date.now()` to control a
 * gesture's duration in a test makes every `waitFor` in the same file
 * un-expirable, which is a hang rather than a failure.
 */
export function gestureNow() {
    if (typeof performance !== 'undefined' && performance && typeof performance.now === 'function') {
        return performance.now();
    }
    return Date.now();
}

/**
 * Clamp a raw finger delta to what the track can honestly show.
 *
 * Two separate limits, which happen to coincide, so one expression serves both:
 *
 *   - Spec §2.3, normative: a strict clamp at the ends of the track, with NO
 *     rubber-band past the boundary. On the first slide the track cannot be
 *     pulled down at all; on the last it cannot be pulled up.
 *   - The slot window (spec §4.4) holds exactly the active slide and one
 *     neighbour either side, and SMASH-1978 recycles it on COMMIT rather than
 *     mid-gesture — so past one viewport in either direction there is no
 *     populated slot to reveal. Dragging further would expose an empty slot,
 *     which is the "torn neighbour" failure this bound exists to prevent.
 *
 * One viewport is therefore the travel limit in both directions, and it is also
 * exactly the distance that "one slide per gesture" allows. Nothing here needs
 * to know about multi-slide travel because nothing can produce it.
 */
export function clampDragOffset(rawOffset, { index, total, viewportHeight }) {
    const reach = viewportHeight > 0 ? viewportHeight : 0;
    // Down reveals the previous slide; up reveals the next. At either end of
    // the track the corresponding limit collapses to zero.
    const maxDown = index > 0 ? reach : 0;
    const maxUp = index < total - 1 ? reach : 0;
    let clamped = rawOffset;
    if (rawOffset > maxDown) clamped = maxDown;
    else if (rawOffset < -maxUp) clamped = -maxUp;
    // Negating a zero limit yields `-0`, which is a distinct value under
    // Object.is and would leak into every comparison and assertion downstream
    // for no benefit whatsoever. Normalise it to a plain zero here, once,
    // rather than making every caller tolerate it.
    return clamped === 0 ? 0 : clamped;
}

/**
 * Where to land when the finger lifts: `1` for the next slide, `-1` for the
 * previous, `0` to return to the one already showing.
 *
 * The primary rule is AREA, per SMASH-1978: the release snaps to whichever
 * slide occupies most of the viewport. Because the track is a contiguous strip
 * of viewport-height slides, that reduces to a single comparison — the adjacent
 * slide covers exactly `|dragOffset|` of the viewport and the current one covers
 * the remaining `viewportHeight - |dragOffset|`, so the adjacent slide holds the
 * majority precisely when `|dragOffset| > viewportHeight / 2`.
 *
 * The boundary is therefore DERIVED from the viewport, never a tuned constant —
 * there is no number to pick, and a "majority" is a strict inequality, so an
 * exact half-and-half split returns rather than advancing. That matters: a
 * gesture that ends exactly on the midpoint has not expressed a preference, and
 * returning is the recoverable choice.
 *
 * The flick accelerator is layered ON TOP as a deliberate composition, not a
 * fallback: a short fast flick advances even though it never uncovered half the
 * viewport, because that is what a flick means to a thumb. This is the one part
 * of the rule that is a product judgement rather than geometry, and it changes
 * pre-SMASH-1978 behaviour in both directions — a fast 61px flick still
 * advances (as it always did), but a SLOW 61px drag now returns where it used
 * to advance. Flagged for maintainer ratification.
 */
export function decideSnap({ dragOffset, viewportHeight, durationMs }) {
    const distance = Math.abs(dragOffset);
    if (distance === 0) return 0;
    // Down (positive) reveals the previous slide, so it navigates backwards.
    const direction = dragOffset < 0 ? 1 : -1;

    // Area rule. Guarded on a real height so the function stays total: a
    // zero-height viewport cannot be reasoned about by area, and answering
    // "majority" from a zero denominator would commit on the tiniest movement.
    if (viewportHeight > 0 && distance > viewportHeight / 2) return direction;

    // Flick accelerator.
    if (distance > SWIPE_THRESHOLD_PX && durationMs < SWIPE_MAX_DURATION_MS) return direction;

    return 0;
}

/**
 * Whether a finished touch sequence should still count as a tap.
 *
 * Kept here rather than inline in the component so the one number involved
 * lives beside the other two gesture bounds.
 */
export function isTap(dragOffset) {
    return Math.abs(dragOffset) <= TAP_SLOP_PX;
}
